import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy,  } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { DialogConfig } from '../../../dialog/dialog-config';
import { DialogRef } from '../../../dialog/dialog-ref';

@Component({
    selector: 'app-kx-ai-reciept-editor',
    templateUrl: './kx-ai-reciept-editor.component.html',
    styleUrls: ['./kx-ai-reciept-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KxAIRecieptEditorComponent implements OnInit, OnDestroy {
  @Input() receiptData: any;
  @Input() documentId: any;
  receiptForm: FormGroup;
  private destroyed$ = new Subject<void>();
  private collapsedCategories = new Set<number>();
  actualDate: any;

  // Confidence & validation
  private overallConfidence: number | null = null;
  private fieldConfidenceMap = new Map<string, number>();   // 'receiptId' -> 0.95
  private itemConfidenceMap  = new Map<string, number>();   // 'chargeDetails:0' -> 0.9, 'chargeDetails.0.lineItems:2' -> 0.7
  aiReportedTotals: { grossAmount: number | null; netPayableAmount: number | null; categorySubtotals: (number | null)[] } =
    { grossAmount: null, netPayableAmount: null, categorySubtotals: [] };
  financialGaps: Array<{ label: string; computed: number; reported: number; diff: number }> = [];
  remarksArray: string[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogConfig:DialogConfig,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private dialogRef: DialogRef
  ) {
    if(this.dialogConfig.data?.json != undefined){
      this.receiptData = this.dialogConfig.data?.json;
    }
  }

  ngOnInit() {
    this.initForm();
    this.setupCalculations();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initForm() {
    // Initialize the form before any template access
    this.updateDates();

    // Capture AI-reported totals BEFORE updateBillingSummary() overwrites them with computed values.
    // Use null (not 0) so we can distinguish "AI didn't return it" from "AI reported 0".
    const bs = this.receiptData?.billingSummary;
    this.aiReportedTotals = {
      grossAmount:       bs?.grossAmount       != null ? bs.grossAmount       : null,
      netPayableAmount:  bs?.netPayableAmount  != null ? bs.netPayableAmount  : null,
      categorySubtotals: (this.receiptData?.chargeDetails || []).map((c: any) =>
        c.categorySubtotal != null ? c.categorySubtotal : null
      )
    };

    // Parse confidence — handle JSON-stringified object (Gemini quirk)
    let rawConfidence = this.receiptData?.confidence;
    if (typeof rawConfidence === 'string') {
      try { rawConfidence = JSON.parse(rawConfidence); } catch { rawConfidence = null; }
    }
    this.overallConfidence = rawConfidence?.overall ?? null;

    // Build fieldConfidenceMap from object: {key: score}
    this.fieldConfidenceMap.clear();
    const fields = rawConfidence?.fields;
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      Object.entries(fields).forEach(([key, score]) => {
        if (score != null) this.fieldConfidenceMap.set(key, score as number);
      });
    }

    // items no longer returned by the LLM — clear the map
    this.itemConfidenceMap.clear();

    // Normalize remarks — LLM may return a real array, a JSON-stringified array, or a plain string
    const rawRemarks = this.receiptData?.remarks;
    if (Array.isArray(rawRemarks)) {
      this.remarksArray = rawRemarks.filter(Boolean);
    } else if (typeof rawRemarks === 'string' && rawRemarks.trim()) {
      const trimmed = rawRemarks.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          this.remarksArray = Array.isArray(parsed) ? parsed.filter(Boolean) : [trimmed];
        } catch {
          this.remarksArray = [trimmed];
        }
      } else {
        this.remarksArray = [trimmed];
      }
    }

    this.receiptForm = this.fb.group({
      receiptId: [this.receiptData?.receiptId || '', Validators.required],
      billDate: [this.receiptData?.billDate || '', Validators.required],
      patientDetails: this.createPatientDetailsForm(),
      chargeDetails: this.fb.array([]),
      billingSummary: this.createBillingSummaryForm(),
      insuranceDetails: this.createInsuranceDetailsForm(),
      remarks: [Array.isArray(rawRemarks) ? rawRemarks.join('\n') : (rawRemarks || '')]
    });

    // Initialize charge details after form is created
    const chargeDetailsArray = this.createChargeDetails();
    chargeDetailsArray.forEach(category => {
      this.chargeDetails.push(category);
    });

    // On initial load: keep AI-provided categorySubtotal / billing summary values intact.
    // Only compute the "approved" columns (not returned by AI) from line items.
    this.chargeDetails.controls.forEach((_, index) => {
      this.updateApprovedCategorySubtotal(index);
    });
    this.initApprovedSummary();
  }

  updateDates(){
    if(this.receiptData && this.receiptData.billDate){
      this.actualDate = this.receiptData.billDate.replace(/Z$/, '');
      this.receiptData.billDate = new Date(this.receiptData.billDate.replace(/Z$/, '')).toLocaleDateString('en-CA');
    }
    if(this.receiptData && this.receiptData.patientDetails && this.receiptData.patientDetails.admissionDetails && this.receiptData.patientDetails.admissionDetails.admissionDate){
      this.receiptData.patientDetails.admissionDetails.admissionDate = this.receiptData.patientDetails.admissionDetails.admissionDate.replace(/Z$/, "").substring(0,16);
    }
    if(this.receiptData && this.receiptData.patientDetails && this.receiptData.patientDetails.admissionDetails && this.receiptData.patientDetails.admissionDetails.dischargeDate){
      this.receiptData.patientDetails.admissionDetails.dischargeDate = this.receiptData.patientDetails.admissionDetails.dischargeDate.replace(/Z$/, "").substring(0,16);
    }

  }

  private createPatientDetailsForm() {
    const pd = this.receiptData?.patientDetails || {};
    return this.fb.group({
      age: [pd?.age || ''],
      gender: [pd?.gender || ''],
      address: [pd?.address || ''],
      contactNumber: [pd?.contactNumber || ''],
      patientId: [pd?.patientId || ''],
      admissionDetails: this.createAdmissionDetailsForm(pd)
    });
  }

  private createAdmissionDetailsForm(pd: any) {
    const diagnosis = Array.isArray(pd?.admissionDetails?.diagnosis) 
      ? pd.admissionDetails.diagnosis 
      : [pd?.admissionDetails?.diagnosis].filter(Boolean);

    return this.fb.group({
      admissionNumber: [pd?.admissionDetails?.admissionNumber || ''],
      admissionDate: [pd?.admissionDetails?.admissionDate || ''],
      dischargeDate: [pd?.admissionDetails?.dischargeDate || ''],
      admittedUnderDoctor: [pd?.admissionDetails?.admittedUnderDoctor || ''],
      referringDoctor: [pd?.admissionDetails?.referringDoctor || ''],
      roomOrBedNumber: [pd?.admissionDetails?.roomOrBedNumber || ''],
      diagnosis: this.fb.array(diagnosis.map(d => this.fb.control(d))),
      reasonForAdmission: [pd?.admissionDetails?.reasonForAdmission || '']
    });
  }

  private createChargeDetails() {
    return (this.receiptData?.chargeDetails || []).map(c => this.createChargeCategory(c));
  }

  private createChargeCategory(category?: any): FormGroup {
    const categoryGroup = this.fb.group({
      categoryName: [category?.categoryName || '', Validators.required],
      lineItems: this.fb.array(
        (category?.lineItems || []).map(item => this.createLineItem(item))
      ),
      categorySubtotal: [category?.categorySubtotal || 0],
      approvedCategorySubtotal: [category?.approvedCategorySubtotal || 0]
    });

    // Subscribe to changes in line items array
    (categoryGroup.get('lineItems') as FormArray).valueChanges.pipe(
      takeUntil(this.destroyed$),
      debounceTime(300)
    ).subscribe(() => {
      const index = this.chargeDetails.controls.indexOf(categoryGroup);
      if (index !== -1) {
        this.updateCategorySubtotal(index);
        this.updateApprovedCategorySubtotal(index);
        this.updateBillingSummary();
      }
    });

    return categoryGroup;
  }

  toDateInputValue(value: any): string | null {
    if (!value) return null;
  
    // Already a Date
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
  
    // Timestamp
    if (typeof value === 'number') {
      return new Date(value).toISOString().slice(0, 10);
    }
  
    // ISO or JS-parseable string
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
  
    // Fallback: DD-MM-YYYY or DD/MM/YYYY
    const match = value.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      return `${yyyy}-${mm}-${dd}`;
    }
  
    // Give up gracefully
    console.warn('Unrecognized date format:', value);
    return null;
  }
  

  private createLineItem(item?: any): FormGroup {
    const amount = item?.amount || 0;
    const quantity = item?.quantity || 1;
    const unitPrice = item?.unitPrice || (quantity ? amount / quantity : 0);
    // Set approved amount equal to original amount by default if not provided
    const approvedAmount = item?.approvedAmount ?? amount;

    const itemGroup = this.fb.group({
      itemDate: [this.toDateInputValue(item?.itemDate)],
      itemDescription: [item?.itemDescription || '', Validators.required],
      class: [item?.class || ''],
      itemCode: [item?.itemCode || ''],
      quantity: [quantity],
      unitPrice: [unitPrice],
      discount: [item?.discount || 0], // Added discount field
      amount: [amount],
      approvedAmount: [approvedAmount],
      doctorName: [item?.doctorName || null],
      batchNumber: [item?.batchNumber || null],
      expiryDate: [this.toDateInputValue(item?.expiryDate)],
      notes: [item?.notes || null]
    });

    // Subscribe to changes in approved amount
    itemGroup.get('approvedAmount').valueChanges.pipe(
      takeUntil(this.destroyed$),
      debounceTime(300)
    ).subscribe(() => {
      const categoryIndex = this.findCategoryIndexForLineItem(itemGroup);
      if (categoryIndex !== -1) {
        this.updateApprovedCategorySubtotal(categoryIndex);
        this.updateBillingSummary();
      }
    });

    return itemGroup;
  }

  private findCategoryIndexForLineItem(lineItem: FormGroup): number {
    return this.chargeDetails.controls.findIndex(category => {
      const lineItems = category.get('lineItems') as FormArray;
      return lineItems.controls.some(item => item === lineItem);
    });
  }

  private createBillingSummaryForm() {
    const bs = this.receiptData?.billingSummary || {};
    return this.fb.group({
      grossAmount: [bs.grossAmount || 0],
      approvedGrossAmount: [bs.approvedGrossAmount || 0],
      totalDiscount: [bs.totalDiscount || 0],
      serviceCharge: [bs.serviceCharge || 0],
      amountAfterDiscount: [bs.amountAfterDiscount || 0],
      concession: [bs.concession || 0],
      refundedAmount: [bs.refundedAmount || 0],
      roundOff: [bs.roundOff || 0],
      taxes: this.fb.array((bs.taxes || []).map(t => this.createTaxGroup(t))),
      advancePaid: [bs.advancePaid || 0],
      netPayableAmount: [bs.netPayableAmount || 0],
      approvedNetPayableAmount: [bs.approvedNetPayableAmount || 0],
      amountInWords: [bs.amountInWords || ''],
      currency: [bs.currency || 'INR']
    });
  }

  private createTaxGroup(t?: any) {
    return this.fb.group({
      taxType: [t?.taxType],
      taxAmount: [t?.taxAmount]
    });
  }

  private createInsuranceDetailsForm() {
    const insurance = this.receiptData?.insuranceDetails || {};
    return this.fb.group({
      note: [insurance.note || '']
    });
  }

  private setupCalculations() {
    this.chargeDetails.controls.forEach((category, catIndex) => {
      const lineItems = category.get('lineItems') as FormArray;
      lineItems.controls.forEach((item, itemIndex) => {
        this.setupLineItemListeners(catIndex, itemIndex);
      });
    });

    // Also update billing summary when service charge, advance, etc. changes
    const billingSummary = this.receiptForm.get('billingSummary');
    ['serviceCharge', 'advancePaid', 'concession', 'roundOff'].forEach(field => {
      billingSummary.get(field).valueChanges.pipe(
        takeUntil(this.destroyed$),
        debounceTime(300)
      ).subscribe(() => {
        this.updateBillingSummary();
      });
    });
  }

  private setupLineItemListeners(catIndex: number, itemIndex: number) {
    const itemGroup = this.getLineItem(catIndex, itemIndex);
    
    ['quantity', 'unitPrice', 'discount'].forEach(controlName => {
        itemGroup.get(controlName).valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(300)
        ).subscribe(() => this.updateCalculatedAmounts(catIndex, itemIndex));
    });

    itemGroup.get('approvedAmount').valueChanges.pipe(
      takeUntil(this.destroyed$),
      debounceTime(300)
    ).subscribe(() => this.updateApprovedTotals(catIndex));
  }

  private updateCalculatedAmounts(catIndex: number, itemIndex: number) {
    const itemGroup = this.getLineItem(catIndex, itemIndex);
    const quantity = parseFloat(itemGroup.get('quantity').value) || 0;
    const unitPrice = parseFloat(itemGroup.get('unitPrice').value) || 0;
    const discount = parseFloat(itemGroup.get('discount').value) || 0;
    const calculatedAmount = (quantity * unitPrice) - discount;
    
    // Only update amount if we have valid numbers
    if (!isNaN(calculatedAmount)) {
      itemGroup.get('amount').setValue(Math.max(0, calculatedAmount), { emitEvent: false });
      // If there's no approved amount yet, set it to match the calculated amount
      if (itemGroup.get('approvedAmount').value === null || itemGroup.get('approvedAmount').value === undefined) {
        itemGroup.get('approvedAmount').setValue(Math.max(0, calculatedAmount), { emitEvent: false });
      }
    }
    
    this.updateCategorySubtotal(catIndex);
    // Billing summary is updated via the FormArray valueChanges listener
  }

  private updateApprovedTotals(catIndex: number) {
    this.updateApprovedCategorySubtotal(catIndex);
    this.updateBillingSummary();
  }

  private updateCategorySubtotal(catIndex: number) {
    const lineItems = this.chargeDetails.at(catIndex).get('lineItems') as FormArray;
    const subtotal = lineItems.controls.reduce((sum, item) => {
      const amount = parseFloat(item.get('amount').value) || 0;
      return sum + amount;
    }, 0);
    // Round to 2 decimal places to avoid floating point precision issues
    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    this.chargeDetails.at(catIndex).get('categorySubtotal').setValue(roundedSubtotal, { emitEvent: false });
  }

  private updateApprovedCategorySubtotal(catIndex: number) {
    const lineItems = this.chargeDetails.at(catIndex).get('lineItems') as FormArray;
    const subtotal = lineItems.controls.reduce((sum, item) => {
      const approvedAmount = parseFloat(item.get('approvedAmount').value) || 0;
      return sum + approvedAmount;
    }, 0);
    // Round to 2 decimal places to avoid floating point precision issues
    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    this.chargeDetails.at(catIndex).get('approvedCategorySubtotal').setValue(roundedSubtotal, { emitEvent: false });
  }

  // Called only on initial load — preserves AI-reported billing summary values and
  // computes only the "approved" columns (approvedGrossAmount, approvedNetPayableAmount)
  // which are derived fields not returned by the AI.
  private initApprovedSummary() {
    let approvedGrossAmount = 0;
    this.chargeDetails.controls.forEach(category => {
      const lineItems = category.get('lineItems') as FormArray;
      lineItems.controls.forEach(item => {
        approvedGrossAmount += parseFloat(item.get('approvedAmount').value) || 0;
      });
    });

    const bs = this.receiptForm.get('billingSummary');
    const serviceCharge  = parseFloat(bs.get('serviceCharge').value)  || 0;
    const advance        = parseFloat(bs.get('advancePaid').value)     || 0;
    const concession     = parseFloat(bs.get('concession').value)      || 0;
    const roundOff       = parseFloat(bs.get('roundOff').value)        || 0;
    const totalDiscount  = parseFloat(bs.get('totalDiscount').value)   || 0;

    const approvedNetPayableAmount = Math.max(0, Math.round(
      ((approvedGrossAmount + serviceCharge) - (totalDiscount + concession + advance + roundOff)) * 100
    ) / 100);

    bs.patchValue({
      approvedGrossAmount:      Math.round(approvedGrossAmount * 100) / 100,
      approvedNetPayableAmount
    }, { emitEvent: false });

    this.computeFinancialGaps();
    this.cdr.detectChanges();
  }

  private updateBillingSummary() {
    let grossAmount = 0;
    let approvedGrossAmount = 0;
    let totalDiscount = 0;

    this.chargeDetails.controls.forEach(category => {
      const lineItems = category.get('lineItems') as FormArray;
      lineItems.controls.forEach(item => {
        const amount = parseFloat(item.get('amount').value) || 0;
        const discount = parseFloat(item.get('discount').value) || 0;
        const approvedAmount = parseFloat(item.get('approvedAmount').value) || 0;

        grossAmount += amount;
        totalDiscount += discount;
        approvedGrossAmount += approvedAmount;
      });
    });

    const billingSummary = this.receiptForm.get('billingSummary');
    const serviceCharge = parseFloat(billingSummary.get('serviceCharge').value) || 0;
    const advance = parseFloat(billingSummary.get('advancePaid').value) || 0;
    const concession = parseFloat(billingSummary.get('concession').value) || 0;
    const roundOff = parseFloat(billingSummary.get('roundOff').value) || 0;

    const amountAfterDiscount = Math.max(0, grossAmount - totalDiscount);

    // Net Payable is based on the amount after line-item discounts.
    const netPayableAmount = Math.max(0, Math.round(((amountAfterDiscount + serviceCharge) - (concession + advance + roundOff)) * 100) / 100);
    
    // Approved Net Payable is based on the approved gross amount, less other deductions.
    const approvedNetPayableAmount = Math.max(0, Math.round(((approvedGrossAmount + serviceCharge) - (totalDiscount + concession + advance + roundOff)) * 100) / 100);

    billingSummary.patchValue({
      grossAmount: Math.round(grossAmount * 100) / 100,
      approvedGrossAmount: Math.round(approvedGrossAmount * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      amountAfterDiscount: Math.round(amountAfterDiscount * 100) / 100,
      netPayableAmount: netPayableAmount,
      approvedNetPayableAmount: approvedNetPayableAmount
    }, { emitEvent: false });
    this.computeFinancialGaps();
    this.cdr.detectChanges();
  }

  // Form array accessors and helper methods
  get chargeDetails(): FormArray {
    return this.receiptForm.get('chargeDetails') as FormArray;
  }

  get formattedRemarks(): SafeHtml {
    const remarksValue = this.receiptForm.get('remarks').value as string;
    if (!remarksValue || typeof remarksValue !== 'string') {
      return '';
    }
  
    // Use a regex with a positive lookahead to split the string before each number pattern (e.g., "2. ")
    // This keeps the number with the line item for further processing.
    const lines = remarksValue.split(/\s*(?=\d+\.)/).filter(line => line.trim() !== '');
  
    if (lines.length === 0) {
      // If the split results in nothing, it might be a single unnumbered line.
      if (remarksValue.trim()) {
        return this.sanitizer.bypassSecurityTrustHtml(`<ol><li>${remarksValue.trim()}</li></ol>`);
      }
      return '';
    }
  
    const listItems = lines.map(line => {
      // Trim and remove the leading number pattern (e.g., "1. ") before wrapping in <li>
      const cleanedLine = line.trim().replace(/^\d+\.\s*/, '');
      return `<li>${cleanedLine}</li>`;
    }).join('');
  
    const html = `<ol>${listItems}</ol>`;
    
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private getLineItem(catIndex: number, itemIndex: number): FormGroup {
    return (this.chargeDetails.at(catIndex).get('lineItems') as FormArray).at(itemIndex) as FormGroup;
  }

  trackByCategory(index: number, category: AbstractControl): AbstractControl {
    return category; // Track by the unique FormGroup object reference
  }

  trackByLineItem(index: number, item: AbstractControl): AbstractControl {
    return item; // Track by the unique FormGroup object reference
  }


  // Add/remove methods
  addChargeCategory() {
    const newIndex = this.chargeDetails.length;
    this.chargeDetails.push(this.createChargeCategory());
    // Ensure the new category is expanded
    this.collapsedCategories.delete(newIndex);
  }

  removeChargeCategory(index: number) {
    this.chargeDetails.removeAt(index);
    this.collapsedCategories.delete(index);
    // Shift all higher indices down by 1
    const newCollapsed = new Set<number>();
    this.collapsedCategories.forEach(i => {
      if (i > index) {
        newCollapsed.add(i - 1);
      } else if (i < index) {
        newCollapsed.add(i);
      }
    });
    this.collapsedCategories = newCollapsed;
    this.updateBillingSummary();
  }

  addLineItem(categoryIndex: number) {
    const lineItems = this.chargeDetails.at(categoryIndex).get('lineItems') as FormArray;
    const newItem = this.createLineItem();
    lineItems.push(newItem);
    this.setupLineItemListeners(categoryIndex, lineItems.length - 1);
  }

  removeLineItem(categoryIndex: number, itemIndex: number) {
    const lineItems = this.chargeDetails.at(categoryIndex).get('lineItems') as FormArray;
    lineItems.removeAt(itemIndex);
    this.updateCategorySubtotal(categoryIndex);
    this.updateApprovedCategorySubtotal(categoryIndex);
    this.updateBillingSummary();
  }

  onSubmit() {
    if(this.dialogConfig && this.dialogConfig['parentFormControls']){
        this.updatecontrols()
      }
      console.log('Form submitted:', this.receiptForm.value);
      // Print the form values
      console.table(this.receiptForm.value);
      this.close()
  }
  updatecontrols(){
    let pusharr = []
    let paymentPushArray = []
    let documentPushArray = []
    let millis = 0;
    this.receiptForm.value.chargeDetails.forEach(element => {
      element.lineItems.forEach(lineElement => {
        if (lineElement && lineElement.itemDate) {
          const [y, m, d] = lineElement.itemDate.split('-').map(Number);
          millis = new Date(y, m - 1, d).getTime();
          console.log(millis);
        }
        let setControls = {
          "serviceType": element.categoryName,
          "service_item": lineElement.itemDescription,
          "quantity": lineElement.quantity,
          "amount": lineElement.amount,
          "serviceDate": millis,
          "discount_amount": lineElement.discount_amount?lineElement.discount_amount:0
        }
        pusharr.push(setControls);
      });
    });
    this.dialogConfig['parentFormControls'].bill_details.setValue(pusharr)
    this.dialogConfig['parentFormControls'].total_service_amount.setValue(this.receiptForm.value.billingSummary.grossAmount)
    this.dialogConfig['parentFormControls'].bill_no.setValue(this.receiptForm.value.receiptId)
    this.dialogConfig['parentFormControls'].encounter_id.setValue(this.receiptForm.value.patientDetails.admissionDetails.admissionNumber)
    this.setDateControls();
    this.receiptData.paymentDetails.forEach(element => {
      let setPaymentControls = {
        "payment_mode_id": element.modeOfPayment? element.modeOfPayment.toLowerCase().replace(/\s+/g, '-') : "cash",
        "payment_mode": element.modeOfPayment? element.modeOfPayment: "Cash",
        // "bank_name":
        // "bank_code"
        "received_amount": element.amountPaid,
        "transaction_no": element.receiptNumber
      }
      paymentPushArray.push(setPaymentControls);
    });
    let setDocumentControls = {
      "document_id": this.documentId,
      "document_name": "document.pdf",
      "folder_name": "Bill"
    }
    documentPushArray.push(setDocumentControls)
    this.dialogConfig['parentFormControls'].bill_documents.setValue(documentPushArray)
    this.dialogConfig['parentFormControls'].payment_detail.setValue(paymentPushArray)
    this.dialogConfig['parentFormControls'].net_amount_summary.setValue(this.receiptForm.value.billingSummary.netPayableAmount)
    this.dialogConfig['parentFormControls'].total_discount_amount.setValue(this.receiptForm.value.billingSummary.totalDiscount)
    this.dialogConfig['parentFormControls'].refunded_amount.setValue(this.receiptForm.value.billingSummary.refundedAmount)
  }

  setDateControls(){
    if(this.actualDate){
      let updatedBillDate = new Date(this.actualDate).getTime();
      this.dialogConfig['parentFormControls'].bill_date_time.setValue(updatedBillDate)
    }
    if(this.receiptForm && this.receiptForm.value && this.receiptForm.value.patientDetails && this.receiptForm.value.patientDetails.admissionDetails && this.receiptForm.value.patientDetails.admissionDetails.dischargeDate){
      let updatedDischargeDate = new Date(this.receiptForm.value.patientDetails.admissionDetails?.dischargeDate).getTime();
      this.dialogConfig['parentFormControls'].discharge_date_time.setValue(updatedDischargeDate)
    }
    if(this.receiptForm && this.receiptForm.value && this.receiptForm.value.patientDetails && this.receiptForm.value.patientDetails.admissionDetails && this.receiptForm.value.patientDetails.admissionDetails.admissionDate){
      let updatedAdmissionDate = new Date(this.receiptForm.value.patientDetails.admissionDetails?.admissionDate).getTime();
      this.dialogConfig['parentFormControls'].admission_date_time.setValue(updatedAdmissionDate)
    }
  }

  close() {
    this.dialogRef.close();
  }

  toggleCategory(index: number) {
    if (this.collapsedCategories.has(index)) {
      this.collapsedCategories.delete(index);
    } else {
      this.collapsedCategories.add(index);
    }
  }

  isCategoryCollapsed(index: number): boolean {
    return this.collapsedCategories.has(index);
  }

  expandAllCategories() {
    this.collapsedCategories.clear();
  }

  collapseAllCategories() {
    for (let i = 0; i < this.chargeDetails.length; i++) {
      this.collapsedCategories.add(i);
    }
  }

  confirmDeleteCategory(index: number, event: Event) {
    event.stopPropagation();
    if (confirm('Are you sure you want to delete this category and all its items?')) {
      this.removeChargeCategory(index);
    }
  }

  confirmDeleteLineItem(catIndex: number, itemIndex: number) {
    if (confirm('Are you sure you want to delete this item?')) {
      this.removeLineItem(catIndex, itemIndex);
    }
  }

  // ── Confidence & financial-gap helpers ─────────────────────────────────────

  getFieldConfidence(field: string): number | null {
    const val = this.fieldConfidenceMap.get(field);
    return val !== undefined ? val : null;
  }

  /** path: e.g. 'chargeDetails.0.lineItems' */
  getItemConfidence(path: string, index: number): number | null {
    const val = this.itemConfidenceMap.get(`${path}:${index}`);
    return val !== undefined ? val : null;
  }

  getLineItemConfidence(catIndex: number, itemIndex: number): number | null {
    return this.getItemConfidence(`chargeDetails.${catIndex}.lineItems`, itemIndex);
  }

  getCategoryConfidence(catIndex: number): number | null {
    return this.getItemConfidence('chargeDetails', catIndex);
  }

  getOverallConfidence(): number | null {
    return this.overallConfidence;
  }

  /** Returns a CSS class name based on the confidence score. */
  getConfidenceClass(score: number | null): string {
    if (score === null) return '';
    if (score >= 0.8) return 'confidence-high';
    if (score >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  }

  isLowConfidence(score: number | null): boolean {
    return score !== null && score < 0.7;
  }

  confidencePct(score: number | null): string {
    if (score === null) return '';
    return `${Math.round(score * 100)}%`;
  }

  /** Merges the reimbursement class with confidence classes for ngClass on line items. */
  getLineItemNgClass(itemClassValue: string, catIndex: number, itemIndex: number): { [key: string]: boolean } {
    const score = this.getLineItemConfidence(catIndex, itemIndex);
    const result: { [key: string]: boolean } = {};
    if (itemClassValue) result[itemClassValue] = true;
    if (score !== null) {
      result['confidence-high']   = score >= 0.8;
      result['confidence-medium'] = score >= 0.6 && score < 0.8;
      result['confidence-low']    = score < 0.6;
    }
    return result;
  }

  private computeFinancialGaps() {
    const bs = this.receiptForm?.get('billingSummary')?.value;
    if (!bs) return;

    const gaps: Array<{ label: string; computed: number; reported: number; diff: number }> = [];

    // Only compare when AI actually returned a value (null = AI didn't report it, skip)
    const compare = (label: string, computed: number, reported: number | null) => {
      if (reported === null) return;
      if (Math.abs(computed - reported) > 0.01) {
        gaps.push({ label, computed, reported, diff: parseFloat((computed - reported).toFixed(2)) });
      }
    };

    // Compute totals fresh from extracted line items — the form's grossAmount/netPayable
    // now hold AI-reported values, so we can't use those as the "computed" side.
    let lineItemGross = 0;
    let lineItemDiscount = 0;
    this.chargeDetails?.controls.forEach((cat, i) => {
      const lineItems = cat.get('lineItems') as FormArray;
      let catSum = 0;
      lineItems.controls.forEach(item => {
        catSum          += parseFloat(item.get('amount').value)   || 0;
        lineItemDiscount += parseFloat(item.get('discount').value) || 0;
      });
      lineItemGross += catSum;
      compare(`"${cat.get('categoryName').value}" Subtotal`, catSum, this.aiReportedTotals.categorySubtotals[i] ?? null);
    });

    const serviceCharge = parseFloat(bs.serviceCharge) || 0;
    const advance       = parseFloat(bs.advancePaid)   || 0;
    const concession    = parseFloat(bs.concession)    || 0;
    const roundOff      = parseFloat(bs.roundOff)      || 0;
    const lineItemNet   = Math.max(0, Math.round(
      ((lineItemGross - lineItemDiscount + serviceCharge) - (concession + advance + roundOff)) * 100
    ) / 100);

    compare('Gross Amount', lineItemGross, this.aiReportedTotals.grossAmount);
    compare('Net Payable',  lineItemNet,   this.aiReportedTotals.netPayableAmount);

    this.financialGaps = gaps;
  }
}
